import type * as PIXI from 'pixi.js';

import { Config } from '../video-data/config';

import type { Container } from '../core/asset/Container';
import { DynamicStylingCreationLevel, CreationType } from '../enums';
import type { MergeConstructors, MixinBase, GetInstance, GetConstructorParameters } from '../types';
import { DynamicAnimationConfiguration } from '../libs/DynamicAnimationConfiguration';
import { DynamicGraphicsConfigurator } from '../libs/DynamicGraphicsConfigurator';
import { assertInArray, assertInEnum } from '../validators';
import type { AnimationCurveManager } from '../libs/AnimationCurveManager';
import type { RemoveAnchoredPoints } from '../libs/Curve';
import type { LoadSettings } from './serializableMixin';
import { objectHasTemplateValues } from './attributes/template/common';

/**
 * When inheriting this mixin, the inheritor must implement the following methods.
 * Often these methods are provided by other mixins, however the inheritor may be acting as a proxy and may need to
 * forward these calls to the real parent.
 */
export interface DynamicStylingMixinRequirements {
    childRequestingRedraw(child: Container): void;
    get animationManager(): AnimationCurveManager;
}

type DynamicStyleingMixinInterface = {
    objectAnimation: DynamicAnimationConfiguration[];
    colorGraphics: DynamicGraphicsConfigurator[];
    alphaGraphics: DynamicGraphicsConfigurator[];
    animateOutBehaviour: string;
    animationAnchorsToRemove: RemoveAnchoredPoints;
    hasDynamicStyle: boolean;
    hasTemplateReferenceValues(): boolean;
    hasColorGraphic: boolean;
    hasObjectAnimation: boolean;
    hasAlphaGraphic: boolean;
    getCreationContainersAtLevelForType(level: DynamicStylingCreationLevel, type?: CreationType): PIXI.Container[];
    addObjectAnimation(): DynamicAnimationConfiguration;
    removeObjectAnimation(index: number): void;
    _prepareDynamicStylingForUpdate(): void;
    _updateDynamicStyling(): void;
    addColorGraphic(settings?: Record<string, any>): DynamicGraphicsConfigurator;
    removeColorGraphic(index: number): void;
    addAlphaGraphic(settings?: Record<string, any>): DynamicGraphicsConfigurator;
    removeAlphaGraphic(index: number): void;
    setAutoUpdateOnConfigurators(value: boolean): void;
    dynamicStylingPropertiesToSerialize: string[];
    destroyDynamicStyles(): void;
};

/**
 * This mixin adds support for dynamic styling. Classes that inherit the mixin must implement
 * DynamicStylingMixinRequirements.
 *
 * Classes using this Mixin have a few requirements:
 * - In `destroy()`, call `this.destroyDynamicStyles()`
 * - In `get propertiesToSerialize`, concatenate properties listed in `get dynamicStylingPropertiesToSerialize()`
 * - In `_setAutoDraw(value)`, call `this.setAutoUpdateOnConfigurators(value)`
 * Optional:
 * - Override `getCreationContainersAtLevelForType()` to return applicable containers.
 * @deprecated
 */
export const DynamicStylingMixin = <TBase extends MixinBase>(Base: TBase) => {
    const Derived = class extends (Base as any) {
        _classType = 'DynamicStylingMixin';

        /**
         * A list of animations which operate on the entire asset object. For text assets specifically these animations
         * affect the entire block of text, not specific lines or words.
         */
        objectAnimation: DynamicAnimationConfiguration[] = [];
        colorGraphics: DynamicGraphicsConfigurator[] = [];
        alphaGraphics: DynamicGraphicsConfigurator[] = [];
        #animateOutBehaviour = Config.ANIMATE_OUT_BEHAVIOUR_NEVER;
        #animationAnchorsToRemove: RemoveAnchoredPoints = Config.END_ANIMATION_CURVE_ANCHOR;

        // eslint-disable-next-line no-useless-constructor
        constructor(settings) {
            // eslint-disable-next-line constructor-super
            super(settings);
        }

        /**
         * Returns true if the asset has any configurators which make up a dynamic style. This can be an object
         * animation, color or alpha graphic configuration.
         */
        get hasDynamicStyle(): boolean {
            return this.hasObjectAnimation || this.hasAlphaGraphic || this.hasColorGraphic;
        }

        /** Returns true if any of its styles color or fontUrl properties are driven by the global template */
        hasTemplateReferenceValues(): boolean {
            if (objectHasTemplateValues(this)) {
                return true;
            }
            if (this.colorGraphics.some(graphic => objectHasTemplateValues(graphic.shape))) {
                return true;
            }
            if (this.alphaGraphics.some(graphic => objectHasTemplateValues(graphic.shape))) {
                return true;
            }
            return false;
        }

        /**
         * True if the asset contains any color graphic configurators
         */
        get hasColorGraphic(): boolean {
            return this.colorGraphics.length > 0;
        }

        get animationAnchorsToRemove() {
            return this.#animationAnchorsToRemove;
        }

        set animationAnchorsToRemove(value) {
            if (this.animationAnchorsToRemove === value) {
                return;
            }
            assertInArray(value, Config.ANIMATION_CURVE_ANCHORS);
            if (this.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS && value === Config.END_ANIMATION_CURVE_ANCHOR) {
                throw new Error(`${this.name}'s animate out behaviour does not allow removing the end anchor`);
            }
            this.#animationAnchorsToRemove = value;
            this.requiresRedraw = true;
        }

        /**
         * Returns True if the asset has any `DynamicAnimationConfiguration`s.
         */
        get hasObjectAnimation(): boolean {
            return this.objectAnimation.length > 0;
        }

        /**
         * True if the asset contains any alpha graphic configurators
         */
        get hasAlphaGraphic(): boolean {
            return this.alphaGraphics.length > 0;
        }

        /**
         * Defines the allowed behaviour of all exit animations for this class.
         * If ALWAYS is set, then the asset will ensure no end anchor points are removed.
         * IF WHEN_PRECEDING is set, then the asset will allow end anchor points to be removed
         * by calling removeOutAnimationIfAllowed
         */
        get animateOutBehaviour(): string {
            return this.#animateOutBehaviour;
        }

        set animateOutBehaviour(value: string) {
            if (this.animateOutBehaviour === value) {
                return;
            }
            assertInArray(value, Config.ANIMATE_OUT_BEHAVIOURS);
            this.#animateOutBehaviour = value;
            switch (this.#animateOutBehaviour) {
                case Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS:
                    this.#animationAnchorsToRemove = Config.NONE_ANIMATION_CURVE_ANCHOR;
                    break;
                case Config.ANIMATE_OUT_BEHAVIOUR_NEVER:
                case Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING:
                    this.#animationAnchorsToRemove = Config.END_ANIMATION_CURVE_ANCHOR;
                    break;
            }
            this.requiresRedraw = true;
        }

        /**
         * Returns the creation containers for the given level used for dynamic styling. Currently there are 3 different
         * levels: object, line and word. It is possible for an asset to only support one level. In that case, an empty
         * array will be returned for the unsupported level.
         */
        getCreationContainersAtLevelForType(level: DynamicStylingCreationLevel, type = CreationType.Animation): PIXI.Container[] {
            assertInEnum(type, CreationType);
            let result = [];
            switch (level) {
                case DynamicStylingCreationLevel.Object: result = [this]; break;
                default:
                    throw new Error(`Unsupported container of type ${level}`);
            }
            return result;
        }

        /**
         * This prepares dynamic styles for update by removing any existing assets from the
         * container hierarchy. In order to complete the dynamic style update, _updateDynamicStyling
         * should be called after this. Any calculations which should happen independent of dynamic
         * styles should happen in between these two steps.
         */
        _prepareDynamicStylingForUpdate() {
            this.alphaGraphics.forEach(configurator => configurator.prepareForUpdate());
            this.colorGraphics.forEach(configurator => configurator.prepareForUpdate());
        }

        _updateDynamicStyling() {
            this._updateObjectAnimation();
            this.alphaGraphics.forEach(configurator => configurator.collectProperties());
            this.colorGraphics.forEach(configurator => configurator.collectProperties());
            this.alphaGraphics.forEach(configurator => configurator.createOrUpdateGraphics());
            this.colorGraphics.forEach(configurator => configurator.createOrUpdateGraphics());
        }

        /**
         * Adds a `DynamicAnimationConfiguration` to the `objectAnimation` list. The added configurator can be configured to
         * animate any attribute on the asset. Check `DynamicAnimationConfiguration` for details.
         */
        addObjectAnimation(): DynamicAnimationConfiguration {
            const animationConfigurator = new DynamicAnimationConfiguration({
                parent: this,
                redrawParentOnUpdate: true,
            });
            // do not mutate, we need to create a new reference for React
            this.objectAnimation = [...this.objectAnimation, animationConfigurator];
            this.requiresRedraw = true;
            return animationConfigurator;
        }

        /**
         * Remove the DynamicAnimationConfiguration at the given index
         */
        removeObjectAnimation(index: number) {
            this.objectAnimation.splice(index, 1);
            this.objectAnimation = [...this.objectAnimation];
            this.requiresRedraw = true;
        }

        _updateObjectAnimation() {
            this.objectAnimation.forEach(configurator => {
                configurator.anchorsToRemove = this.animationAnchorsToRemove;
                if (!configurator.enabled) {
                    return;
                }
                const animationCurveConfig = configurator.buildConfig();
                this.animationManager.add(animationCurveConfig);
            });
        }

        _destroyObjectAnimation() {
            this.objectAnimation = [];
        }

        _objectAnimationCustomLoad(properties: Record<string, any>[]) {
            if (!properties) {
                return;
            }
            this.objectAnimation.splice(properties.length);
            properties.forEach((dataObj, i) => {
                const configurator = this.objectAnimation[i] ?? this.addObjectAnimation();
                configurator.load(dataObj);
            });
        }

        /**
         * Adds a color graphic configurator
         * @param settings Additional settings to pass through to the configurator.
         */
        addColorGraphic(settings = {}): DynamicGraphicsConfigurator {
            const configurator = new DynamicGraphicsConfigurator({
                ...settings,
                parent: this,
                redrawParentOnUpdate: true,
            });
            // do not mutate, we need to create a new reference for React
            this.colorGraphics = [...this.colorGraphics, configurator];
            this.requiresRedraw = true;
            return configurator;
        }

        /**
         * Removes the color graphic configurator with the given index
         */
        removeColorGraphic(index: number) {
            this.colorGraphics[index].destroy();
            this.colorGraphics.splice(index, 1);
            this.colorGraphics = [...this.colorGraphics];
            this.requiresRedraw = true;
        }

        _destroyColorGraphics() {
            this.colorGraphics.forEach(graphic => graphic.destroy());
            this.colorGraphics = [];
        }

        _colorGraphicsCustomLoad(properties: Record<string, any>[], settings = { skipTemplateAttributes: false }) {
            if (!properties) {
                return;
            }
            for (let i = properties.length; i < this.colorGraphics.length; i++) {
                this.colorGraphics[i].destroy();
            }
            this.colorGraphics.splice(properties.length);

            properties.forEach((dataObj, index) => {
                const configurator = this.colorGraphics[index] ?? this.addColorGraphic();
                configurator.load(dataObj, settings);
            });
        }

        /**
         * Adds a alpha graphic configurator
         * @param settings Additional settings to pass through to the configurator.
         */
        addAlphaGraphic(settings = {}): DynamicGraphicsConfigurator {
            const configurator = new DynamicGraphicsConfigurator({
                ...settings,
                parent: this,
                redrawParentOnUpdate: true,
            });
            // do not mutate, we need to create a new reference for React
            this.alphaGraphics = [...this.alphaGraphics, configurator];
            this.requiresRedraw = true;
            return configurator;
        }

        /**
         * Removes the alpha graphic configurator with the given index
         */
        removeAlphaGraphic(index: number) {
            this.alphaGraphics[index].destroy();
            this.alphaGraphics.splice(index, 1);
            this.alphaGraphics = [...this.alphaGraphics];
            this.requiresRedraw = true;
        }

        _destroyAlphaGraphics() {
            this.alphaGraphics.forEach(graphic => graphic.destroy());
            this.alphaGraphics = [];
        }

        _alphaGraphicsCustomLoad(properties: Record<string, any>[], settings: LoadSettings) {
            if (!properties) {
                return;
            }
            for (let i = properties.length; i < this.alphaGraphics.length; i++) {
                this.alphaGraphics[i].destroy();
            }
            this.alphaGraphics.splice(properties.length);

            properties.forEach((dataObj, index) => {
                const configurator = this.alphaGraphics[index] ?? this.addAlphaGraphic();
                configurator.load(dataObj, settings);
            });
        }

        setAutoUpdateOnConfigurators(value: boolean) {
            // propagate to dynamic styles, we don't want to process updates at this stage which is
            // why we write to _autoUpdate instead of to autoUpdate (just like _autoDraw above)
            this.objectAnimation.forEach(configurator => { configurator._autoUpdate = value; });
            this.alphaGraphics.forEach(configurator => { configurator._autoUpdate = value; });
            this.colorGraphics.forEach(configurator => { configurator._autoUpdate = value; });
        }

        /**
         * @inheritDoc
         */
        get dynamicStylingPropertiesToSerialize() {
            return [
                'objectAnimation',
                'colorGraphics',
                'alphaGraphics',
                'animateOutBehaviour',
            ];
        }

        destroyDynamicStyles() {
            this._destroyObjectAnimation();
            this._destroyAlphaGraphics();
            this._destroyColorGraphics();
        }
    };

    return Derived as unknown as MergeConstructors<typeof Derived, TBase>;
};


/**
 * Mixin which handles all serializing (loading and saving) abilities of Luminary's
 * classes.
 */
export const TypedDynamicStylingMixin = function<Base>(base: Base) {
    /**
     * @lends Luminary.SerializableMixin
     */
    const Derived = DynamicStylingMixin(base as any);

    type ReturnType = DynamicStyleingMixinInterface & GetInstance<Base>;

    return Derived as unknown as new (props: GetConstructorParameters<typeof Derived>) => ReturnType;
};
